import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./LandingLegalLotteries.module.css";

/** Velocidad del desplazamiento continuo (px por segundo). */
const SPEED_PX_PER_S = 42;
const NUDGE_MS = 420;
const DRAG_THRESHOLD_PX = 5;

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * Carrusel continuo (marquee) de derecha a izquierda.
 * - Duplica los items lo necesario para que el bucle no tenga saltos.
 * - Se pausa al pasar el mouse, al enfocar con teclado o al arrastrar.
 * - Flechas ‹ › desplazan una tarjeta.
 * - Con prefers-reduced-motion no se mueve solo (las flechas siguen funcionando).
 *
 * @param {object} props
 * @param {object[]} props.items
 * @param {(item: object, opts: { hidden: boolean }) => React.ReactNode} props.renderItem
 * @param {(item: object, index: number) => string} props.getKey
 * @param {boolean} props.reduceMotion
 */
const LotteryMarquee = ({ items, renderItem, getKey, reduceMotion }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const groupRef = useRef(null);
  const offsetRef = useRef(0);
  const groupWidthRef = useRef(0);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const nudgeRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const reduceMotionRef = useRef(reduceMotion);
  const [copies, setCopies] = useState(2);

  useEffect(() => {
    reduceMotionRef.current = reduceMotion;
  }, [reduceMotion]);

  /* Mide el ancho de un grupo y calcula cuántas copias se necesitan. */
  useEffect(() => {
    const measure = () => {
      const group = groupRef.current;
      const viewport = viewportRef.current;
      if (!group || !viewport) return;
      const groupWidth = group.getBoundingClientRect().width;
      groupWidthRef.current = groupWidth;
      if (groupWidth > 0) {
        const needed = Math.max(2, Math.ceil(viewport.clientWidth / groupWidth) + 1);
        setCopies((prev) => (prev === needed ? prev : needed));
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    if (groupRef.current) observer.observe(groupRef.current);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [items]);

  /* Bucle de animación. */
  useEffect(() => {
    let frame = 0;
    let last = 0;

    const wrap = () => {
      const width = groupWidthRef.current;
      if (width <= 0) return;
      let shift = 0;
      while (offsetRef.current + shift <= -width) shift += width;
      while (offsetRef.current + shift > 0) shift -= width;
      if (shift !== 0) {
        offsetRef.current += shift;
        if (nudgeRef.current) {
          nudgeRef.current.from += shift;
          nudgeRef.current.to += shift;
        }
        if (dragRef.current) dragRef.current.startOffset += shift;
      }
    };

    const tick = (time) => {
      const dt = last ? Math.min((time - last) / 1000, 0.05) : 0;
      last = time;

      const nudge = nudgeRef.current;
      if (nudge) {
        const progress = Math.min((time - nudge.start) / nudge.duration, 1);
        offsetRef.current = nudge.from + (nudge.to - nudge.from) * easeOutCubic(progress);
        if (progress >= 1) nudgeRef.current = null;
      } else if (
        !reduceMotionRef.current &&
        !hoverRef.current &&
        !focusRef.current &&
        !dragRef.current
      ) {
        offsetRef.current -= SPEED_PX_PER_S * dt;
      }

      wrap();
      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const step = useCallback(() => {
    const first = groupRef.current?.firstElementChild;
    if (!first) return 0;
    const gap = parseFloat(getComputedStyle(groupRef.current).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }, []);

  const nudge = (direction) => {
    const distance = step() * direction;
    if (!distance) return;
    const from = offsetRef.current;
    const base = nudgeRef.current ? nudgeRef.current.to : from;
    if (reduceMotionRef.current) {
      nudgeRef.current = null;
      offsetRef.current = base - distance;
      return;
    }
    nudgeRef.current = {
      from,
      to: base - distance,
      start: performance.now(),
      duration: NUDGE_MS,
    };
  };

  /* Arrastre con mouse/touch (horizontal); el scroll vertical sigue nativo. */
  const onPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    nudgeRef.current = null;
    dragRef.current = {
      startX: event.clientX,
      startOffset: offsetRef.current,
      moved: false,
    };
    suppressClickRef.current = false;

    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) drag.moved = true;
      if (drag.moved) offsetRef.current = drag.startOffset + dx;
    };
    const onUp = () => {
      if (dragRef.current?.moved) suppressClickRef.current = true;
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const onClickCapture = (event) => {
    if (suppressClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    }
  };

  const onBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) focusRef.current = false;
  };

  return (
    <>
      <div
        ref={viewportRef}
        className={styles.marquee}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") hoverRef.current = true;
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") hoverRef.current = false;
        }}
        onFocus={() => {
          focusRef.current = true;
        }}
        onBlur={onBlur}
        onPointerDown={onPointerDown}
        onClickCapture={onClickCapture}
        onDragStart={(e) => e.preventDefault()}
      >
        <div ref={trackRef} className={styles.marqueeTrack}>
          {Array.from({ length: copies }, (_, copy) => (
            <div
              key={copy}
              ref={copy === 0 ? groupRef : undefined}
              className={styles.marqueeGroup}
              aria-hidden={copy > 0 ? "true" : undefined}
            >
              {items.map((item, index) => (
                <div key={`${copy}-${getKey(item, index)}`} className={styles.slide}>
                  {renderItem(item, { hidden: copy > 0 })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.navRow}>
        <button
          type="button"
          className={styles.navBtn}
          aria-label="Ver loterías anteriores"
          onClick={() => nudge(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          className={styles.navBtn}
          aria-label="Ver loterías siguientes"
          onClick={() => nudge(1)}
        >
          ›
        </button>
      </div>
    </>
  );
};

export default LotteryMarquee;
